import React, { useEffect, useId, useRef, useState } from 'react';
import { CloudOff, Cpu, ShieldCheck, Wifi, WifiOff } from 'lucide-react';
import { analyticsService } from '../services/analyticsService';

interface LocalOnlyBadgeProps {
  theme: 'light' | 'dark';
}

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/**
 * The "nothing leaves your device" USP as an always-visible, verifiable
 * disclosure: explains what runs locally and invites an airplane-mode test.
 */
export const LocalOnlyBadge: React.FC<LocalOnlyBadgeProps> = ({ theme }) => {
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const online = useOnlineStatus();
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const toggle = () => {
    if (!open) analyticsService.trackFeatureUsage({ featureName: 'local_only_badge', action: 'open' });
    setOpen((value) => !value);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-black uppercase tracking-wider transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
          isDark
            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
            : 'border-emerald-600/30 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
        }`}
      >
        <ShieldCheck size={14} aria-hidden />
        <span>100% Local</span>
        <span className="sr-only">: how LocalDiff handles your data</span>
      </button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-label="How LocalDiff handles your data"
          className={`absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] z-[130] rounded-2xl border p-4 shadow-2xl text-sm animate-slide-down ${
            isDark ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-700'
          }`}
        >
          <p className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Nothing you compare leaves this device.</p>
          <ul className="flex flex-col gap-2.5 text-xs">
            <li className="flex gap-2">
              <Cpu size={14} className="shrink-0 mt-0.5 text-emerald-500" aria-hidden />
              <span>Diffing, merging and report exports run entirely in your browser.</span>
            </li>
            <li className="flex gap-2">
              <CloudOff size={14} className="shrink-0 mt-0.5 text-emerald-500" aria-hidden />
              <span>Text you paste and files you open are never uploaded, logged or stored on a server.</span>
            </li>
            <li className="flex gap-2">
              {online ? (
                <Wifi size={14} className="shrink-0 mt-0.5 text-blue-500" aria-hidden />
              ) : (
                <WifiOff size={14} className="shrink-0 mt-0.5 text-emerald-500" aria-hidden />
              )}
              <span aria-live="polite">
                {online
                  ? "Don't take our word for it: switch off Wi-Fi and keep comparing."
                  : "You're offline, and LocalDiff is still working."}
              </span>
            </li>
          </ul>
          <a href="/privacy" className="inline-block mt-3 text-xs font-bold text-blue-500 hover:underline">
            Read the privacy policy
          </a>
        </div>
      )}
    </div>
  );
};

export default LocalOnlyBadge;
